// src/pages/MyListings.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Plus, Filter, Search, Edit, Trash2, Eye, Zap, 
  Package, CheckCircle, Clock, Archive, MoreVertical,
  Grid, List, ChevronDown, TrendingUp, DollarSign
} from 'lucide-react';
import PageLayout from '../../components/layout/PageLayout';
import ProductGrid from '../home/ProductGrid';
import { useAuth } from '../../context/AuthContext';
import { getMyListings, updateListing, deleteListing, getBoostPackages } from '../../services/api';
import Loading from '../../components/common/Loading';

const MyListings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    sold: 0,
    draft: 0,
    pending: 0,
    revenue: 0
  });

  const filters = [
    { id: 'all', label: 'All Listings', icon: Package, color: 'text-gray-600' },
    { id: 'active', label: 'Active', icon: CheckCircle, color: 'text-green-600' },
    { id: 'sold', label: 'Sold', icon: DollarSign, color: 'text-blue-600' },
    { id: 'draft', label: 'Drafts', icon: Clock, color: 'text-yellow-600' },
    { id: 'pending', label: 'Pending', icon: Clock, color: 'text-orange-600' },
    { id: 'archived', label: 'Archived', icon: Archive, color: 'text-gray-500' },
  ];

  useEffect(() => {
    if (user) {
      fetchUserListings();
    }
  }, [user]);

  const fetchUserListings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getMyListings(user.user_id);
      
      if (response.data) {
        setListings(response.data);
        calculateStats(response.data);
      } else {
        setError('Failed to load listings');
      }
    } catch (err) {
      // console.error('Error fetching listings:', err);
      setError('Failed to load listings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (listingsData) => {
    const statsData = {
      total: listingsData.length,
      active: listingsData.filter(l => l.status === 'active').length,
      sold: listingsData.filter(l => l.status === 'sold').length,
      draft: listingsData.filter(l => l.status === 'draft').length,
      pending: listingsData.filter(l => l.status === 'pending').length,
      revenue: listingsData
        .filter(l => l.status === 'sold')
        .reduce((sum, l) => sum + (parseFloat(l.price) || 0), 0)
    };
    setStats(statsData);
  };

  // Filter and search listings
  useEffect(() => {
    let result = listings;
    
    // Apply status filter
    if (activeFilter !== 'all') {
      result = result.filter(listing => listing.status === activeFilter);
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(listing => 
        listing.title.toLowerCase().includes(query) ||
        listing.description.toLowerCase().includes(query) ||
        listing.category_name.toLowerCase().includes(query)
      );
    }
    
    setFilteredListings(result);
  }, [listings, activeFilter, searchQuery]);

  const handleEditListing = (listingId) => {
    navigate(`/edit-listing/${listingId}`);
  };

  const handleDeleteListing = async (listingId) => {
    if (window.confirm('Are you sure you want to delete this listing?')) {
      try {
        const response = await deleteListing(listingId);
        if (response.success) {
          setListings(prev => prev.filter(l => l.listing_id !== listingId));
        }
      } catch (err) {
        // console.error('Error deleting listing:', err);
        alert('Failed to delete listing. Please try again.');
      }
    }
  };

  const handleGetBoostPackages = async (listingId) => {
    try {
      const response = await getBoostPackages(listingId);
      if (response.success) {
        // Update the listing in state
        setListings(prev => prev.map(listing => 
          listing.listing_id === listingId 
            ? { ...listing, is_boosted: true } 
            : listing
        ));
      }
    } catch (err) {
      // console.error('Error boosting listing:', err);
    }
  };

  const handleStatusChange = async (listingId, newStatus) => {
    try {
      const response = await updateListing(listingId, { status: newStatus });
      if (response.success) {
        setListings(prev => prev.map(listing => 
          listing.listing_id === listingId 
            ? { ...listing, status: newStatus } 
            : listing
        ));
      }
    } catch (err) {
      // console.error('Error updating listing status:', err);
    }
  };

  if (loading) {
    return (
      <PageLayout title="My Listings">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loading />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="My Listings" 
      subtitle="Manage your products and services"
      rightAction={
        <Link
          to="/create-listing"
          className="flex items-center px-4 py-2 bg-[#ff660e] text-white rounded-xl font-medium hover:bg-[#e55a0d] transition-all shadow-sm hover:shadow"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Listing
        </Link>
      }
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-[#0f0e0e]">{stats.total}</p>
            </div>
            <Package className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Sold</p>
              <p className="text-2xl font-bold text-blue-600">{stats.sold}</p>
            </div>
            <DollarSign className="w-8 h-8 text-blue-400" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Drafts</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.draft}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-400" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
            </div>
            <Clock className="w-8 h-8 text-orange-400" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Revenue</p>
              <p className="text-2xl font-bold text-[#ff660e]">
                ₦{stats.revenue.toLocaleString()}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-[#ff660e]" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Filter Tabs */}
          <div className="flex overflow-x-auto pb-2 md:pb-0 scrollbar-hide">
            <div className="flex space-x-2">
              {filters.map(filter => {
                const Icon = filter.icon;
                return (
                  <button
                    key={filter.id}
                    onClick={() => setActiveFilter(filter.id)}
                    className={`flex items-center px-4 py-2 rounded-xl whitespace-nowrap transition-all ${
                      activeFilter === filter.id
                        ? 'bg-[#ff660e] text-white shadow-sm'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className={`w-4 h-4 mr-2 ${activeFilter === filter.id ? 'text-white' : filter.color}`} />
                    {filter.label}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search your listings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Listings Grid */}
      <div className="mb-6">
        <ProductGrid 
          listings={filteredListings} 
          viewMode="grid"
          isUserListings={true}
          onEdit={handleEditListing}
          onDelete={handleDeleteListing}
          onBoost={handleGetBoostPackages}
          onStatusChange={handleStatusChange}
        />
      </div>

      {/* Empty State */}
      {filteredListings.length === 0 && !loading && (
        <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
            <Package className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {activeFilter === 'all' ? 'No listings yet' : 'No listings found'}
          </h3>
          <p className="text-gray-600 max-w-md mx-auto mb-6">
            {activeFilter === 'all' 
              ? "Start selling by creating your first listing"
              : `No ${activeFilter} listings match your search`}
          </p>
          {activeFilter === 'all' && (
            <Link
              to="/create-listing"
              className="inline-flex items-center px-6 py-3 bg-[#ff660e] text-white rounded-xl font-medium hover:bg-[#e55a0d] transition-all shadow-sm hover:shadow"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create First Listing
            </Link>
          )}
        </div>
      )}

      {/* Quick Actions Footer */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h3 className="text-lg font-semibold text-[#0f0e0e] mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/create-listing')}
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#ff660e] hover:bg-orange-50 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2 text-gray-600" />
            <span className="font-medium">Create New Listing</span>
          </button>
          
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center justify-center p-4 border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <Zap className="w-5 h-5 mr-2 text-yellow-500" />
            <span className="font-medium">Boost Top Listings</span>
          </button>
          
          <button
            onClick={() => navigate('/performance')}
            className="flex items-center justify-center p-4 border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <TrendingUp className="w-5 h-5 mr-2 text-green-500" />
            <span className="font-medium">View Performance</span>
          </button>
        </div>
      </div>
    </PageLayout>
  );
};

export default MyListings;